import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import {
  getExpirationHistory,
  ExpirationHistoryItem,
  ExpirationHistoryResponse,
} from '../../../services/company-credits';
import { toastHandler } from '../../../utils/toastHandler';
import moment from 'moment';
import { useAppSelector } from '../../../store';
import { getCompaniesList } from '../../../services/companies-list';

const ExpirationHistory = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ExpirationHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<{ totalExpiredCredits: number } | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const isAdmin = userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3';

  // Fetch companies list for admin
  useEffect(() => {
    if (isAdmin) {
      const fetchCompanies = async () => {
        try {
          setLoadingCompanies(true);
          const response = await getCompaniesList({ limit: 1000 });
          setCompanies(response.data || []);
        } catch (error: any) {
          console.error('Error fetching companies:', error);
          if (error?.message) {
            toastHandler(error);
          }
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [isAdmin]);

  // Fetch expiration history
  const fetchData = async () => {
    try {
      setLoading(true);
      const response: ExpirationHistoryResponse = await getExpirationHistory({
        page: page + 1,
        limit,
        companyId: selectedCompany || undefined,
      });
      setData(response.data);
      setTotal(response.total);
      setSummary(response.summary);
    } catch (error: any) {
      console.error('Error fetching expiration history:', error);
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, limit, selectedCompany]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && data.length === 0) {
    return <Loader />;
  }

  return (
    <Page title="Expiration History">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Expiration History
          </Typography>
          {summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total Expired Credits: <strong>{summary.totalExpiredCredits}</strong>
            </Typography>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isAdmin && (
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={companies.find((c) => c.id === selectedCompany) || null}
              onChange={(event, newValue) => {
                setSelectedCompany(newValue?.id || null);
                setPage(0);
              }}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Company"
                  placeholder="Select a company or leave empty to see all"
                  variant="outlined"
                />
              )}
              sx={{ minWidth: 300 }}
            />
          )}
        </Box>

        <Card>
          <CardContent>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    {isAdmin && <TableCell>Company</TableCell>}
                    <TableCell>Package Name</TableCell>
                    <TableCell>Expired Credits</TableCell>
                    <TableCell>Used Credits</TableCell>
                    <TableCell>Total Credits</TableCell>
                    <TableCell>Expired At</TableCell>
                    <TableCell>Processed At</TableCell>
                    <TableCell>Package Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No expiration history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((item) => (
                      <TableRow key={item.id}>
                        {isAdmin && (
                          <TableCell>
                            {item.companyName || 'N/A'}
                            {item.companyEmail && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.companyEmail}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.packageName || 'Unknown Package'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.expiredCredits}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.usedCredits ?? 'N/A'}</TableCell>
                        <TableCell>{item.totalCredits ?? 'N/A'}</TableCell>
                        <TableCell>
                          {moment(item.expiredAt).format('YYYY-MM-DD')}
                        </TableCell>
                        <TableCell>
                          {moment(item.processedAt).format('YYYY-MM-DD HH:mm')}
                        </TableCell>
                        <TableCell>
                          {item.packageTotalCredits !== undefined ? (
                            <Box>
                              <Typography variant="body2">
                                Total: {item.packageTotalCredits}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Used: {item.packageUsedCredits ?? 'N/A'} | Allocated: {item.packageAllocatedToAgents ?? 0}
                              </Typography>
                              {item.packagePurchasedAt && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Purchased: {moment(item.packagePurchasedAt).format('YYYY-MM-DD')}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default ExpirationHistory;

